import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  Container,
  Button,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from '@mui/material';
import {
  People as PeopleIcon,
  VideoLibrary as VideoLibraryIcon,
  PendingActions as PendingIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Token as TokenIcon,
  Category as CategoryIcon,
  ArrowForward as ArrowForwardIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import AdminPageWrapper from './components/AdminPageWrapper';

function AdminDashboardHome() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detect if we're in director context (directors should not have navigation from cards)
  const isDirectorContext = location.pathname.startsWith('/director');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalVideos: 0,
    pendingVideos: 0,
    approvedVideos: 0,
    rejectedVideos: 0,
    pendingWithdrawals: 0,
    totalWithdrawals: 0,
    totalAdPayments: 0,
    activeVideos: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentVideos, setRecentVideos] = useState([]);
  const [recentWithdrawals, setRecentWithdrawals] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      // Use new optimized stats endpoint for videos (no need to fetch 1000 records)
      const [usersRes, videoStatsRes, videosRes, withdrawalsRes, adPaymentsRes] = await Promise.all([
        api.get(API.adminUsers).catch((err) => {
          console.error('❌ Failed to fetch users:', err);
          return { data: { users: [] } };
        }),
        api.get(API.adminVideoStats).catch((err) => {
          console.error('❌ Failed to fetch video stats:', {
            error: err,
            message: err.message,
            response: err.response?.data,
            status: err.response?.status,
            url: API.adminVideoStats
          });
          return { data: { stats: {} } };
        }),
        // Still fetch recent videos for display (limit to 5)
        api.get(`${API.adminVideos}?limit=5`).catch((err) => {
          console.error('❌ Failed to fetch recent videos:', err);
          return { data: { videos: [] } };
        }),
        api.get(API.adminWithdrawals).catch((err) => {
          console.error('❌ Failed to fetch withdrawals:', err);
          return { data: { withdrawals: [] } };
        }),
        api.get(API.adminAdPayments).catch((err) => {
          console.error('❌ Failed to fetch ad payments:', err);
          return { data: { payments: [] } };
        }),
      ]);

      const users = usersRes.data?.users || [];
      const videoStats = videoStatsRes.data?.stats || {};
      const videos = videosRes.data?.videos || [];
      const withdrawals = withdrawalsRes.data?.withdrawals || [];
      const adPayments = adPaymentsRes.data?.payments || adPaymentsRes.data || [];

      // Debug: Log all responses
      console.log('📊 Dashboard Data Response:', {
        usersCount: users.length,
        videoStatsFull: videoStatsRes.data,
        videoStats: videoStats,
        videosCount: videos.length,
        withdrawalsCount: withdrawals.length,
        adPaymentsCount: Array.isArray(adPayments) ? adPayments.length : 0,
        allResponses: {
          users: usersRes.data,
          videoStats: videoStatsRes.data,
          videos: videosRes.data,
          withdrawals: withdrawalsRes.data,
          adPayments: adPaymentsRes.data
        }
      });

      // Filter recent videos for display (only active ones)
      const activeVideosList = videos.filter(v => v.IsActive === true || v.IsActive === 1);
      const pendingWithdrawals = withdrawals.filter(w => 
        w.Status === 'Pending' || w.Status === 'pending'
      ).length;

      setStats({
        totalUsers: users.length,
        totalVideos: videoStats.activeVideos || 0, // Use stats from stored procedure
        pendingVideos: videoStats.pendingVideos || 0,
        approvedVideos: videoStats.approvedVideos || 0,
        rejectedVideos: videoStats.rejectedVideos || 0,
        pendingWithdrawals,
        totalWithdrawals: withdrawals.length,
        totalAdPayments: Array.isArray(adPayments) ? adPayments.length : 0,
        activeVideos: videoStats.activeVideos || 0,
      });

      // Get recent items (last 5)
      setRecentUsers(users.slice(0, 5));
      setRecentVideos(activeVideosList.slice(0, 5));
      setRecentWithdrawals(withdrawals.slice(0, 5));
    } catch (err) {
      console.error('❌ Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Platform Overview Stats (similar to "Previous Month Engagement")
  // Colors match user dashboard: Green, Blue, Orange, Purple
  const platformStats = [
    {
      icon: <PeopleIcon />,
      label: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      color: '#4caf50',
      tooltip: 'Total number of registered users on the platform.',
      onClick: isDirectorContext ? undefined : () => navigate('/admin/users'),
    },
    {
      icon: <VideoLibraryIcon />,
      label: 'Total Videos',
      value: stats.totalVideos.toLocaleString(),
      color: '#2196f3',
      tooltip: 'Total number of videos uploaded to the platform.',
      onClick: isDirectorContext ? undefined : () => navigate('/admin/videos'),
    },
    {
      icon: <PendingIcon />,
      label: 'Pending Approvals',
      value: stats.pendingVideos.toLocaleString(),
      color: '#ff9800',
      tooltip: 'Number of videos awaiting admin approval.',
      onClick: isDirectorContext ? undefined : () => navigate('/admin/pending-videos'),
    },
    {
      icon: <WalletIcon />,
      label: 'Pending Withdrawals',
      value: stats.pendingWithdrawals.toLocaleString(),
      color: '#9c27b0',
      tooltip: 'Number of withdrawal requests pending processing.',
      onClick: isDirectorContext ? undefined : () => navigate('/admin/withdrawals?status=Pending'),
    },
  ];

  // Current Period Stats (similar to "Current Month Engagement")
  // Colors match user dashboard: Green, Orange, Blue, Purple
  const currentStats = [
    {
      icon: <CheckCircleIcon />,
      label: 'Active Videos',
      value: stats.activeVideos.toLocaleString(),
      color: '#4caf50',
      tooltip: 'Number of videos currently active and viewable.',
    },
    {
      icon: <CheckCircleIcon />,
      label: 'Approved Videos',
      value: stats.approvedVideos.toLocaleString(),
      color: '#ff9800',
      tooltip: 'Total number of videos that have been approved.',
    },
    {
      icon: <CancelIcon />,
      label: 'Rejected Videos',
      value: stats.rejectedVideos.toLocaleString(),
      color: '#2196f3',
      tooltip: 'Total number of videos that have been rejected.',
    },
    {
      icon: <TrendingUpIcon />,
      label: 'Total Withdrawals',
      value: stats.totalWithdrawals.toLocaleString(),
      color: '#9c27b0',
      tooltip: 'Total number of withdrawal requests processed.',
      onClick: isDirectorContext ? undefined : () => navigate('/admin/withdrawals'),
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      case 'Processing': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <AdminPageWrapper title="Admin Dashboard" subtitle="Loading dashboard data...">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper 
      title="Admin Dashboard" 
      subtitle="Overview of your platform's key metrics and recent activity"
    >
      <Container maxWidth="lg" sx={{ px: 0 }}>
        {/* Platform Overview */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          Platform Overview
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
          {platformStats.map((stat, index) => (
            <Tooltip key={index} title={stat.tooltip} placement="top" enterTouchDelay={0}>
              <Paper
                elevation={0}
                onClick={stat.onClick}
                sx={(theme) => ({
                  p: 3,
                  borderRadius: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(10px)',
                  border:
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255,255,255,0.08)'
                      : '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  cursor: stat.onClick ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow:
                      theme.palette.mode === 'dark'
                        ? '0 8px 25px rgba(0,0,0,0.4)'
                        : '0 8px 25px rgba(0,0,0,0.15)',
                  },
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={(theme) => ({
                      p: 1.5,
                      borderRadius: 2,
                      background:
                        theme.palette.mode === 'dark'
                          ? `${stat.color}30`
                          : `${stat.color}15`,
                      color: stat.color,
                      mr: 2,
                    })}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      fontWeight: 600,
                      letterSpacing: 0.2,
                      color:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.9)'
                          : theme.palette.text.secondary,
                    })}
                  >
                    {stat.label}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                  {stat.value}
                </Typography>
              </Paper>
            </Tooltip>
          ))}
        </Box>

        {/* Current Period Stats */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mt: 4, mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          Current Period Stats
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
          {currentStats.map((stat, index) => (
            <Tooltip key={`current-${index}`} title={stat.tooltip} placement="top" enterTouchDelay={0}>
              <Paper
                elevation={0}
                onClick={stat.onClick}
                sx={(theme) => ({
                  p: 3,
                  borderRadius: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(10px)',
                  border:
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255,255,255,0.08)'
                      : '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  cursor: stat.onClick ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow:
                      theme.palette.mode === 'dark'
                        ? '0 8px 25px rgba(0,0,0,0.4)'
                        : '0 8px 25px rgba(0,0,0,0.15)',
                  },
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={(theme) => ({
                      p: 1.5,
                      borderRadius: 2,
                      background:
                        theme.palette.mode === 'dark'
                          ? `${stat.color}30`
                          : `${stat.color}15`,
                      color: stat.color,
                      mr: 2,
                    })}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      fontWeight: 600,
                      letterSpacing: 0.2,
                      color:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.9)'
                          : theme.palette.text.secondary,
                    })}
                  >
                    {stat.label}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                  {stat.value}
                </Typography>
              </Paper>
            </Tooltip>
          ))}
        </Box>

        {/* Quick Actions */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          Quick Actions
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 4 }}>
          {[
            { label: 'Approve Videos', icon: <CheckCircleIcon />, color: '#4caf50', onClick: () => navigate('/admin/pending-videos') },
            { label: 'Process Withdrawals', icon: <PaymentIcon />, color: '#2196f3', onClick: () => navigate('/admin/withdrawals?status=Pending') },
            { label: 'Manage Users', icon: <PeopleIcon />, color: '#ff9800', onClick: () => navigate('/admin/users') },
            { label: 'Token Rewards', icon: <TokenIcon />, color: '#9c27b0', onClick: () => navigate('/admin/token-rewards') },
            { label: 'Ad Payments', icon: <PaymentIcon />, color: '#4caf50', onClick: () => navigate('/admin/ad-payments-list') },
            { label: 'Video Categories', icon: <CategoryIcon />, color: '#2196f3', onClick: () => navigate('/admin/video-categories') },
          ].map((action, index) => {
            // Disable navigation for directors
            const actionWithDisabledNav = {
              ...action,
              onClick: isDirectorContext ? undefined : action.onClick
            };
            return (
            <Paper
              key={index}
              elevation={0}
              onClick={actionWithDisabledNav.onClick}
              sx={(theme) => ({
                p: 2.5,
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.3s ease',
                cursor: actionWithDisabledNav.onClick ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': actionWithDisabledNav.onClick ? {
                  transform: 'translateY(-4px)',
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? '0 8px 25px rgba(0,0,0,0.4)'
                      : '0 8px 25px rgba(0,0,0,0.15)',
                } : {},
              })}
            >
              <Box
                sx={(theme) => ({
                  p: 1.5,
                  borderRadius: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? `${actionWithDisabledNav.color}30`
                      : `${actionWithDisabledNav.color}15`,
                  color: actionWithDisabledNav.color,
                })}
              >
                {actionWithDisabledNav.icon}
              </Box>
              <Typography
                variant="body1"
                sx={(theme) => ({
                  fontWeight: 600,
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.9)'
                      : theme.palette.text.primary,
                  flex: 1,
                })}
              >
                {actionWithDisabledNav.label}
              </Typography>
              <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Paper>
            );
          })}
        </Box>

        {/* Recent Activity */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          Recent Activity
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(255,255,255,0.2)',
                height: '100%',
              })}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: (theme) => theme.palette.text.primary,
                }}
              >
                Recent Users
              </Typography>
              {loading ? (
                <CircularProgress size={24} />
              ) : recentUsers.length > 0 ? (
                <Stack spacing={1}>
                  {recentUsers.map((user) => (
                    <Box
                      key={user.UserID}
                      onClick={isDirectorContext ? undefined : () => navigate(`/admin/user-details/${user.UserID}`)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        cursor: isDirectorContext ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': isDirectorContext ? {} : {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Avatar sx={{ bgcolor: '#4caf50', width: 32, height: 32, fontSize: '0.8rem' }}>
                        {user.UserName?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {user.UserName || user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent users.
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(255,255,255,0.2)',
                height: '100%',
              })}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: (theme) => theme.palette.text.primary,
                }}
              >
                Recent Videos
              </Typography>
              {loading ? (
                <CircularProgress size={24} />
              ) : recentVideos.length > 0 ? (
                <Stack spacing={1}>
                  {recentVideos.map((video) => (
                    <Box
                      key={video.VideoID}
                      onClick={isDirectorContext ? undefined : () => navigate(`/admin/videos?videoId=${video.VideoID}`)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        cursor: isDirectorContext ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': isDirectorContext ? {} : {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <VisibilityIcon
                        color={video.IsActive ? 'success' : 'error'}
                        fontSize="small"
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {video.Title || 'Untitled'}
                        </Typography>
                        <Chip
                          label={video.Status || 'Pending'}
                          size="small"
                          color={getStatusColor(video.Status)}
                          sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent videos.
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(255,255,255,0.2)',
                height: '100%',
              })}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: (theme) => theme.palette.text.primary,
                }}
              >
                Recent Withdrawals
              </Typography>
              {loading ? (
                <CircularProgress size={24} />
              ) : recentWithdrawals.length > 0 ? (
                <Stack spacing={1}>
                  {recentWithdrawals.map((withdrawal) => (
                    <Box
                      key={withdrawal.WithdrawalID || withdrawal.WithdrawalRequestID}
                      onClick={isDirectorContext ? undefined : () => navigate('/admin/withdrawals')}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        cursor: isDirectorContext ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': isDirectorContext ? {} : {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <WalletIcon
                        fontSize="small"
                        color={
                          withdrawal.Status === 'Approved' || withdrawal.Status === 'approved'
                            ? 'success'
                            : withdrawal.Status === 'Rejected' || withdrawal.Status === 'rejected'
                            ? 'error'
                            : 'warning'
                        }
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {withdrawal.UserName || 'Unknown'} - ${withdrawal.Amount?.toFixed(2) || '0.00'}
                        </Typography>
                        <Chip
                          label={withdrawal.Status || 'Pending'}
                          size="small"
                          color={getStatusColor(withdrawal.Status)}
                          sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent withdrawals.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </AdminPageWrapper>
  );
}

export default AdminDashboardHome;
